/**
 * Back-office — Partenaires sous-traitants (permission partners.manage).
 * Liste des partenaires, onboarding d'un nouveau compte, et déclenchement
 * manuel des rappels de conformité.
 */
import type { PartnerProfileStatus } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  listPartners,
  onboardPartner,
  runComplianceReminders,
} from '@/features/partners/partners-api';

const profileStatusBadge: Record<
  PartnerProfileStatus | 'none',
  { label: string; cls: string }
> = {
  none: { label: 'Sans profil', cls: 'bg-gray-100 text-gray-700' },
  incomplet: { label: 'Incomplet', cls: 'bg-gray-100 text-gray-700' },
  en_verification: { label: 'En vérification', cls: 'bg-amber-100 text-amber-800' },
  valide: { label: 'Validé', cls: 'bg-green-100 text-green-800' },
  suspendu: { label: 'Suspendu', cls: 'bg-red-100 text-red-800' },
};

export function AdminPartnersPage() {
  const qc = useQueryClient();
  const partners = useQuery({ queryKey: ['admin', 'partners'], queryFn: listPartners });

  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
    siret: '',
  });

  const createMut = useMutation({
    mutationFn: () =>
      onboardPartner({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        companyName: form.companyName,
        ...(form.siret ? { siret: form.siret } : {}),
      }),
    onSuccess: () => {
      toast.success('Partenaire créé');
      qc.invalidateQueries({ queryKey: ['admin', 'partners'] });
      setForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        companyName: '',
        siret: '',
      });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const remindersMut = useMutation({
    mutationFn: runComplianceReminders,
    onSuccess: (r) =>
      toast.success(
        `${r.remindersSent} rappel(s) envoyé(s), ${r.expiredMarked} pièce(s) expirée(s)`,
      ),
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <DashboardLayout variant="admin" title="Partenaires">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Partenaires sous-traitants</CardTitle>
              <CardDescription>
                {partners.data
                  ? `${partners.data.length} partenaire(s)`
                  : 'Chargement…'}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={remindersMut.isPending}
              onClick={() => remindersMut.mutate()}
            >
              <BellRing className="mr-2 h-4 w-4" />
              Rappels conformité
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Société</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Conformité</TableHead>
                  <TableHead className="text-right">Détail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.data?.length ? (
                  partners.data.map((p) => {
                    const status = p.profile?.status ?? 'none';
                    return (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">
                          {p.profile?.companyName ?? '—'}
                        </TableCell>
                        <TableCell>
                          {p.firstName} {p.lastName}
                          <span className="block text-xs text-gray-500">{p.email}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={profileStatusBadge[status].cls}>
                            {profileStatusBadge[status].label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button asChild size="sm" variant="outline">
                            <Link to={`/admin/partners/${p.id}`}>Ouvrir</Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                      Aucun partenaire.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Onboarder un partenaire
            </CardTitle>
            <CardDescription>Crée un compte partenaire sous-traitant.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">Prénom</Label>
                <Input
                  id="firstName"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Nom</Label>
                <Input
                  id="lastName"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="companyName">Raison sociale</Label>
              <Input
                id="companyName"
                value={form.companyName}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="siret">SIRET (optionnel)</Label>
              <Input
                id="siret"
                value={form.siret}
                onChange={(e) => setForm({ ...form, siret: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe initial</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <Button
              className="w-full"
              disabled={
                !form.email ||
                !form.password ||
                !form.firstName ||
                !form.lastName ||
                !form.phone ||
                !form.companyName ||
                createMut.isPending
              }
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? 'Création…' : 'Créer le partenaire'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
